import { UseDropdownItems } from "@/common/composition/dropdown-items";
import { UseLoadingAction } from "@/common/composition/loading";
import { InfoTypeHelper, POPULATION_TYPE } from "@/common/models";
import { AxisType, TimeAxisType } from "@/gql/types";
import { state as data_sources_state } from "@/views/customize/data-sources/data/state";
import { Ref, computed, ref } from "@vue/composition-api";
import * as api from "./api";
import { SharedState } from "@/common/state";
import { CountItem } from "./model";
import { exec_only_if_cond_changed, once } from "@/fp";
import { state as overview_state } from "../../../data/state";
type TimeFrameSelection = {
  id: string;
  name: string;
  type: TimeAxisType;
  value: string;
};

const [
  from_time_frames,
  selected_from_time_frame,
  _set_selected_from_time_frame,
  init_time_frames,
] = UseDropdownItems<TimeFrameSelection>([]);

const [loading, set_loading] = UseLoadingAction<
  "init" | "reload" | "changing_severity"
>(false, "init");
const is_loading_all = computed(
  () =>
    loading.value.is_loading &&
    (loading.value.action == "init" || loading.value.action == "reload"),
);

// for information risk card
const selected_severity = ref(3);
const set_selected_severity = async (value: 1 | 2 | 3) => {
  set_loading(true, "changing_severity");
  selected_severity.value = value;
  const l0s = [...SharedState.l0InfoTypes.value.filter((f) => f.code != 1)];
  const res = await api.get_risks_count_for_l0({
    l0s,
    severity: selected_severity.value,
  });
  if (res.success) {
    info_risk_events.value = res.value;
  }
  set_loading(false, "changing_severity");
};

const events_counts_for_l0: Ref<CountItem[]> = ref([]);
const info_risk_events: Ref<CountItem[]> = ref([]);
const data_per_application: Ref<CountItem[]> = ref([]);
const mip_labeling_per_l0: Ref<
  Array<{
    label_id: string;
    label_icon: string;
    counts: {
      total: number;
      ms: number;
      cognni: number;
    };
  }>
> = ref([]);

const information_shared_per_entity_per_label: Ref<
  Array<{
    type: POPULATION_TYPE;
    items: number[];
  }>
> = ref([]);

const source_security_insights: Ref<
  Array<{
    type: "risk_users" | "mass_downloads" | "event_justification";
    counts: CountItem[];
    count_sum: number;
    title: string;
    desc: string;
  }>
> = ref([]);

const count_sum = (items: CountItem[]) =>
  items.reduce((acc, item) => {
    acc += item.count;
    return acc;
  }, 0);

const load_dashboard_data = async () => {
  let apps: string[] = [];

  switch (true) {
    case SharedState.in_motion_services_flags.value.is_ms_org_only:
      apps = ["onedrive", "ms_teams", "exchange_365", "sharepoint"];
      break;
    case SharedState.in_motion_services_flags.value.is_google_org_only:
      apps = ["gmail", "gdrive", "gchat"];
      break;
    case SharedState.in_motion_services_flags.value.is_ms_and_google_org:
      apps = [
        "exchange_365",
        "onedrive",
        "ms_teams",
        "sharepoint",
        "gmail",
        "gdrive",
        "gchat",
      ];
      break;
  }
  /* 
    overview_state.current_overview.value.id == "google_workspace"
      ? ["gmail", "gdrive", "gchat"]
      : ["onedrive", "ms_teams", "exchange_365", "sharepoint"]; */
  set_loading(true, "reload");
  const l0s = [...SharedState.l0InfoTypes.value.filter((f) => f.code != 1)];
  const res = await Promise.all([
    api.get_events_count_for_L0(l0s),
    api.get_counts_per_application({
      l0s,
      apps,
    }),
    api.get_information_shared_per_entity_per_label(l0s.map((d) => d.id)),
    // api.get_relative_mip_counts_per_l0(l0s.map((d) => d.id)),
    api.get_risks_count_for_l0({
      l0s,
      severity: selected_severity.value,
    }),
  ]);
  if (res.every((d) => d.success)) {
    // @ts-ignore
    events_counts_for_l0.value = res[0].value;

    // @ts-ignore
    data_per_application.value = res[1].value;
    // @ts-ignore
    information_shared_per_entity_per_label.value = res[2].value;
    // @ts-ignore
    // mip_labeling_per_l0.value = res[3].value;
    // @ts-ignore
    info_risk_events.value = res[3].value;

    /*  if (SharedState.is_demo_env.value) {
      events_counts_for_l0.value = events_counts_for_l0.value.filter(
        (d) => d.id !== InfoTypeHelper.REGULATED_ID()
      );

      info_risk_events.value = info_risk_events.value.filter(
        (d) => d.id !== InfoTypeHelper.REGULATED_ID()
      );
    } */
  }

  const non_personal_l0 = l0s.filter(
    (f) => f.id !== InfoTypeHelper.REGULATED_ID(),
  );

  const risky_users_res = await api.get_risky_users_results_for_all_l0s(
    non_personal_l0,
    selected_from_time_frame.value!.value,
    selected_from_time_frame.value!.type == TimeAxisType.Month
      ? "Month"
      : "Week",
  );

  const mass_downlaoders_res = await api.get_mass_download_for_all_l0s({
    l0s: non_personal_l0,
    time_frame: selected_from_time_frame.value!.value,
    time_type: selected_from_time_frame.value?.type!,
  });

  const high_risk_events_res = await api.get_high_severity_events_for_all_l0s(
    non_personal_l0,
  );
  if (
    risky_users_res.success &&
    mass_downlaoders_res.success &&
    high_risk_events_res.success
  ) {
    source_security_insights.value = [
      {
        type: "event_justification",
        counts: high_risk_events_res.value,
        count_sum: count_sum(high_risk_events_res.value),
        title: "High Severity Incidents",
        desc: "Justification requests for users who generate high severity information risks.",
      },
      {
        type: "risk_users",
        counts: risky_users_res.value,
        count_sum: count_sum(risky_users_res.value),
        title:
          selected_from_time_frame.value!.type == TimeAxisType.Month
            ? "Top Risky Users"
            : "Last Week's Top Risky Users",
        desc: "Weekly email alerts to your organization's highest risk users",
      },
      {
        type: "mass_downloads",
        counts: mass_downlaoders_res.value,
        count_sum: count_sum(mass_downlaoders_res.value),
        title: "Mass Download Incidents ",
        desc: "Triggers compliance-oriented email alerts",
      },
    ];
  }

  set_loading(false, "reload");
};

const set_selected_from_time_frame = async (time_frame: TimeFrameSelection) => {
  _set_selected_from_time_frame(time_frame.id);
  const s = SharedState.timeFrameOptions.value.find(
    (f) => f.displayName == time_frame.name,
  )!;
  SharedState.setSelectedTimeFrameOption(s);
  await load_dashboard_data();
};

const init = exec_only_if_cond_changed<void, string, void>(
  () => Promise.resolve(overview_state.current_overview.value.id),
  async () => {
    set_loading(true, "init");
    await data_sources_state.init();
    const time_frames =
      data_sources_state.ext.get_in_motion_source_time_frames("motion_365");
    init_time_frames(
      time_frames.map((t) => {
        return {
          id: t.displayName,
          name: t.displayName,
          type: t.type,
          value: t.from,
        };
      }),
    );
    // demo env
    if (SharedState.is_demo_env.value) {
      set_selected_from_time_frame(
        from_time_frames.value.find(
          (d) => d.type == TimeAxisType.Month && d.value == "2024-09-01",
        ) || from_time_frames.value[0],
      );
    } else {
      set_selected_from_time_frame(
        from_time_frames.value.find((d) => d.type == TimeAxisType.Month) ||
          from_time_frames.value[0],
      );
    }

    //set_loading(false, "init");
  },
);

export const state = {
  init,
  loading,
  is_loading_all,
  from_time_frames,
  selected_from_time_frame,
  set_selected_from_time_frame,
  // data
  source_security_insights,
  events_counts_for_l0,
  info_risk_events,
  data_per_application,
  information_shared_per_entity_per_label,
  mip_labeling_per_l0,
  selected_severity,
  set_selected_severity,
};
